import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Plus, Edit2, Users, X, Trash2 } from 'lucide-react';
import { usePermissions } from '../../modules/auth/hooks/usePermissions';
import { PageCard, SectionLabel, TOKENS, ConfirmDialog } from '../../components/design-system';
import {
  listManufacturers,
  createManufacturer,
  updateManufacturer,
  listManufacturerUsers,
  addManufacturerUser,
  removeManufacturerUser,
  createManufacturerUser,
} from './api';
import { Manufacturer } from './types';

const PAGE_SIZE = 50;

export const AdminManufacturersPage: FC = () => {
  const perms = usePermissions();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editor, setEditor] = useState<{ mode: 'create' | 'edit'; entity: Manufacturer | null } | null>(null);
  const [usersOpen, setUsersOpen] = useState<Manufacturer | null>(null);

  const qc = useQueryClient();
  const list = useQuery(
    ['admin-manufacturers', search, page],
    () => listManufacturers({
      search: search.trim() || undefined,
      take: PAGE_SIZE,
      skip: (page - 1) * PAGE_SIZE,
    }),
    { enabled: perms.is_admin_anywhere, keepPreviousData: true },
  );

  if (!perms.is_admin_anywhere) {
    return (
      <PageCard>
        <SectionLabel>Fabricantes</SectionLabel>
        <div style={{ padding: 16, color: TOKENS.color.muted, fontSize: 13 }}>
          Apenas administradores podem gerenciar fabricantes.
        </div>
      </PageCard>
    );
  }

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}>
      <PageCard>
        <SectionLabel>Cadastros</SectionLabel>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: TOKENS.color.text, margin: '4px 0 8px' }}>
          Fabricantes
        </h1>
        <p style={{ marginTop: 0, fontSize: 13, color: TOKENS.color.muted, lineHeight: 1.5, marginBottom: 0 }}>
          Cadastre os fabricantes que produzem caixas e vincule os usuários autorizados a gerenciar
          o estoque de cada um. Apenas usuários vinculados conseguem ver o menu "Estoque (Fabricante)".
        </p>
      </PageCard>

      <PageCard>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <input
            type='text'
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder='🔍 Buscar por nome ou email'
            style={{
              flex: 1, minWidth: 200, padding: '8px 10px', fontSize: 13,
              border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
              background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
            }}
          />
          <button type='button' onClick={() => setEditor({ mode: 'create', entity: null })} style={primaryBtn}>
            <Plus size={14} /> Novo fabricante
          </button>
        </div>

        <div style={{ marginTop: TOKENS.spacing.md }}>
          {list.isLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>Carregando…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>
              Nenhum fabricante cadastrado ainda. Clique em "Novo fabricante" pra começar.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((m) => (
                <div key={m.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 12px', background: TOKENS.color.surface,
                  borderRadius: TOKENS.radius.chip,
                  borderLeft: `3px solid ${m.enabled ? TOKENS.color.severity.low.text : TOKENS.color.muted}`,
                  opacity: m.enabled ? 1 : 0.7,
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.color.text }}>
                      {m.name}
                      {!m.enabled && (
                        <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: TOKENS.color.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          (desativado)
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: TOKENS.color.muted, marginTop: 2 }}>
                      {m.contact_email ?? 'sem email'}
                      {' · '}
                      {m.user_count} usuário(s) vinculado(s) · {m.stock_count} caixa(s) no estoque
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type='button' onClick={() => setUsersOpen(m)} style={iconBtn} title='Gerenciar usuários'>
                      <Users size={14} />
                    </button>
                    <button type='button' onClick={() => setEditor({ mode: 'edit', entity: m })} style={iconBtn} title='Editar'>
                      <Edit2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: TOKENS.spacing.sm }}>
            <button type='button' onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={ghostBtn}>‹ Anterior</button>
            <span style={{ fontSize: 11, fontWeight: 700, color: TOKENS.color.muted, letterSpacing: 0.4 }}>
              {page} / {totalPages} · {total} fabricantes
            </span>
            <button type='button' onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={ghostBtn}>Próxima ›</button>
          </div>
        )}
      </PageCard>

      {editor && (
        <ManufacturerEditorModal
          mode={editor.mode}
          entity={editor.entity}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); qc.invalidateQueries(['admin-manufacturers']); }}
        />
      )}

      {usersOpen && (
        <ManufacturerUsersModal
          manufacturer={usersOpen}
          onClose={() => { setUsersOpen(null); qc.invalidateQueries(['admin-manufacturers']); }}
        />
      )}
    </div>
  );
};

// ----- Editor modal (create + edit) -----

const ManufacturerEditorModal: FC<{
  mode: 'create' | 'edit';
  entity: Manufacturer | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ mode, entity, onClose, onSaved }) => {
  const [name, setName] = useState(entity?.name ?? '');
  const [email, setEmail] = useState(entity?.contact_email ?? '');
  const [phone, setPhone] = useState(entity?.contact_phone ?? '');
  const [notes, setNotes] = useState(entity?.notes ?? '');
  const [enabled, setEnabled] = useState(entity?.enabled ?? true);

  const save = useMutation(
    () => {
      const body = {
        name: name.trim(),
        contact_email: email.trim() || null,
        contact_phone: phone.trim() || null,
        notes: notes.trim() || null,
        enabled,
      };
      return mode === 'edit' && entity ? updateManufacturer(entity.id, body) : createManufacturer(body);
    },
    {
      onSuccess: () => { toast.success(mode === 'edit' ? 'Fabricante atualizado.' : 'Fabricante criado.'); onSaved(); },
      onError: (e: any) => { toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`); },
    },
  );

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 520 }}>
        <PageCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionLabel>{mode === 'edit' ? 'Editar fabricante' : 'Novo fabricante'}</SectionLabel>
            <button type='button' onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
              <X size={18} color={TOKENS.color.muted} />
            </button>
          </div>

          <Field label='Nome (obrigatório)'>
            <input type='text' value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </Field>
          <Field label='Email de contato'>
            <input type='email' value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
          </Field>
          <Field label='Telefone'>
            <input type='text' value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} />
          </Field>
          <Field label='Notas'>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <input type='checkbox' checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span style={{ fontSize: 13, color: TOKENS.color.text }}>Habilitado</span>
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button type='button' onClick={onClose} style={ghostBtn}>Cancelar</button>
            <button
              type='button'
              disabled={!name.trim() || save.isLoading}
              onClick={() => save.mutate()}
              style={{
                ...primaryBtn,
                opacity: (!name.trim() || save.isLoading) ? 0.5 : 1,
                cursor: (!name.trim() || save.isLoading) ? 'not-allowed' : 'pointer',
              }}
            >
              {save.isLoading ? 'Salvando…' : (mode === 'edit' ? 'Salvar' : 'Criar')}
            </button>
          </div>
        </PageCard>
      </div>
    </div>
  );
};

// ----- Users management modal -----

const ManufacturerUsersModal: FC<{
  manufacturer: Manufacturer;
  onClose: () => void;
}> = ({ manufacturer, onClose }) => {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'link' | 'create'>('create');
  const [userIdInput, setUserIdInput] = useState('');
  // Inline-create form state
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newDoc, setNewDoc] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState<{ user_id: number; name: string } | null>(null);

  const usersQuery = useQuery(
    ['admin-manufacturers', manufacturer.id, 'users'],
    () => listManufacturerUsers(manufacturer.id),
  );

  const addMut = useMutation(
    (user_id: number) => addManufacturerUser(manufacturer.id, user_id),
    {
      onSuccess: () => { toast.success('Usuário vinculado.'); setUserIdInput(''); qc.invalidateQueries(['admin-manufacturers', manufacturer.id, 'users']); },
      onError: (e: any) => { toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`); },
    },
  );

  const createMut = useMutation(
    () => createManufacturerUser(manufacturer.id, {
      name: newName.trim(),
      email: newEmail.trim(),
      document_number: newDoc.trim(),
      password: newPassword,
    }),
    {
      onSuccess: (data) => {
        toast.success(`Usuário ${data.email} criado e vinculado.`);
        setNewName(''); setNewEmail(''); setNewDoc(''); setNewPassword('');
        qc.invalidateQueries(['admin-manufacturers', manufacturer.id, 'users']);
      },
      onError: (e: any) => { toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`); },
    },
  );

  const removeMut = useMutation(
    (user_id: number) => removeManufacturerUser(manufacturer.id, user_id),
    {
      onSuccess: () => { toast.success('Usuário desvinculado.'); setRemoveConfirm(null); qc.invalidateQueries(['admin-manufacturers', manufacturer.id, 'users']); },
      onError: (e: any) => { toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`); },
    },
  );

  const users = usersQuery.data ?? [];

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 600 }}>
        <PageCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionLabel>Usuários vinculados a {manufacturer.name}</SectionLabel>
            <button type='button' onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
              <X size={18} color={TOKENS.color.muted} />
            </button>
          </div>

          {/* Mode tabs: criar novo user OR linkar user existente. */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 10, borderBottom: `1px solid ${TOKENS.color.border}` }}>
            {([
              { k: 'create', label: 'Criar usuário fabricante' },
              { k: 'link', label: 'Vincular usuário existente' },
            ] as { k: 'create' | 'link'; label: string }[]).map((tab) => {
              const active = mode === tab.k;
              return (
                <button
                  key={tab.k} type='button' onClick={() => setMode(tab.k)}
                  style={{
                    padding: '8px 12px', fontSize: 12, fontWeight: 700,
                    border: 'none', background: 'transparent',
                    color: active ? TOKENS.color.action : TOKENS.color.muted,
                    borderBottom: `2px solid ${active ? TOKENS.color.action : 'transparent'}`,
                    cursor: 'pointer',
                  }}
                >{tab.label}</button>
              );
            })}
          </div>

          {mode === 'create' ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: TOKENS.color.muted, marginBottom: 8, lineHeight: 1.5 }}>
                Cria o usuário com email + senha. Ele faz login normalmente e vê o menu "Estoque (Fabricante)".
                Não recebe acesso a empresas — perfil é exclusivo de fabricante.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Field label='Nome'>
                  <input type='text' value={newName} onChange={(e) => setNewName(e.target.value)} style={inputStyle} />
                </Field>
                <Field label='Email'>
                  <input type='email' value={newEmail} onChange={(e) => setNewEmail(e.target.value)} style={inputStyle} />
                </Field>
                <Field label='CPF / documento'>
                  <input type='text' value={newDoc} onChange={(e) => setNewDoc(e.target.value)} style={inputStyle} />
                </Field>
                <Field label='Senha (mín. 6)'>
                  <input type='password' value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle} />
                </Field>
              </div>
              <button
                type='button'
                disabled={!newName.trim() || !newEmail.trim() || !newDoc.trim() || newPassword.length < 6 || createMut.isLoading}
                onClick={() => createMut.mutate()}
                style={{
                  ...primaryBtn, marginTop: 8,
                  opacity: (!newName.trim() || !newEmail.trim() || !newDoc.trim() || newPassword.length < 6 || createMut.isLoading) ? 0.5 : 1,
                  cursor: (!newName.trim() || !newEmail.trim() || !newDoc.trim() || newPassword.length < 6 || createMut.isLoading) ? 'not-allowed' : 'pointer',
                }}
              >
                {createMut.isLoading ? 'Criando…' : 'Criar e vincular'}
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: TOKENS.color.muted, marginBottom: 8, lineHeight: 1.5 }}>
                Vincula um usuário que já existe no sistema. Informe o ID do usuário (pegue em "Administradores" ou outras telas).
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type='number'
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value)}
                  placeholder='ID do usuário'
                  style={inputStyle}
                />
                <button
                  type='button'
                  disabled={!userIdInput || addMut.isLoading}
                  onClick={() => addMut.mutate(Number(userIdInput))}
                  style={{
                    ...primaryBtn,
                    opacity: (!userIdInput || addMut.isLoading) ? 0.5 : 1,
                    cursor: (!userIdInput || addMut.isLoading) ? 'not-allowed' : 'pointer',
                  }}
                >Vincular</button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflowY: 'auto' }}>
            {usersQuery.isLoading ? (
              <div style={{ padding: 16, textAlign: 'center', color: TOKENS.color.muted, fontSize: 12 }}>Carregando…</div>
            ) : users.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: TOKENS.color.muted, fontSize: 12 }}>
                Nenhum usuário vinculado ainda.
              </div>
            ) : users.map((u) => (
              <div key={u.user_id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 10px', background: TOKENS.color.surface,
                borderRadius: TOKENS.radius.chip, fontSize: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 700, color: TOKENS.color.text }}>{u.user_name}</div>
                  <div style={{ fontSize: 11, color: TOKENS.color.muted }}>
                    {u.user_email} · ID {u.user_id} · vinculado {new Date(u.linked_at).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <button
                  type='button'
                  onClick={() => setRemoveConfirm({ user_id: u.user_id, name: u.user_name })}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'transparent', border: 'none',
                    color: TOKENS.color.severity.high.text,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Trash2 size={12} /> Desvincular
                </button>
              </div>
            ))}
          </div>
        </PageCard>
      </div>

      <ConfirmDialog
        open={removeConfirm !== null}
        title={`Desvincular ${removeConfirm?.name ?? ''}?`}
        description={`O usuário deixará de ver o estoque de ${manufacturer.name}. As caixas que ele já criou continuam no estoque.`}
        confirmLabel='Desvincular'
        tone='danger'
        loading={removeMut.isLoading}
        onCancel={() => setRemoveConfirm(null)}
        onConfirm={() => removeConfirm && removeMut.mutate(removeConfirm.user_id)}
      />
    </div>
  );
};

// ----- styles -----

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: TOKENS.color.muted }}>
      {label}
    </span>
    {children}
  </label>
);

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
  background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
};
const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: TOKENS.color.action, color: '#fff', border: 'none',
  padding: '6px 12px', fontSize: 12, fontWeight: 700, borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: TOKENS.color.card, color: TOKENS.color.text,
  border: `1px solid ${TOKENS.color.border}`,
  padding: '6px 12px', fontSize: 12, fontWeight: 600,
  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: TOKENS.color.card, color: TOKENS.color.muted,
  border: `1px solid ${TOKENS.color.border}`,
  width: 28, height: 28, borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const modalOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px', zIndex: 1000, overflowY: 'auto',
};

export default AdminManufacturersPage;
